import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Circle, Package, Truck, Home, ChefHat } from "lucide-react";

const STEPS = [
  { key: "pending_kitchen", label: "Received", icon: Package },
  { key: "preparing", label: "In the kitchen", icon: ChefHat },
  { key: "ready", label: "Ready for courier", icon: Package },
  { key: "dispatched", label: "On its way", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

export default function OrderTrack() {
  const { orderNumber } = useParams();
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);

  const load = async (withEmail) => {
    try {
      const q = withEmail ? `?email=${encodeURIComponent(withEmail)}` : "";
      const { data } = await api.get(`/track/${orderNumber}${q}`);
      setData(data);
    } catch { setData(false); }
    setChecked(true);
  };

  useEffect(() => { load(""); }, [orderNumber]);

  if (!checked) return <div className="text-center py-24 text-[color:var(--brunchi-ink-soft)]">Loading…</div>;
  if (data === false) return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="font-display text-2xl">Order not found.</div>
      <p className="text-sm text-[color:var(--brunchi-ink-soft)] mt-2">Please double-check the order number.</p>
    </div>
  );

  const currentIdx = STEPS.findIndex((s) => s.key === data.status);
  const isCancelled = data.status === "cancelled";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="track-page">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">Order tracker</div>
      <h1 className="font-display text-3xl lg:text-4xl tracking-tight">
        <span className="font-mono text-[color:var(--brunchi-ink)]">{data.order_number}</span>
      </h1>
      <p className="text-sm text-[color:var(--brunchi-ink-soft)] mt-1">
        Placed {data.created_at?.slice(0, 10)} · Shipping to {data.shipping_city}, {data.shipping_country}
      </p>

      {isCancelled ? (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <div className="font-display text-xl">This order was cancelled.</div>
        </div>
      ) : (
        <div className="mt-8 bg-white rounded-2xl border border-[color:var(--brunchi-stone)] p-6 sm:p-8">
          <div className="space-y-6">
            {STEPS.map((s, i) => {
              const done = i <= currentIdx;
              const current = i === currentIdx;
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-start gap-4" data-testid={`track-step-${s.key}`}>
                  <div className="relative">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                      done
                        ? "bg-[color:var(--brunchi-green)] text-white"
                        : "bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-ink-soft)]"
                    }`}>
                      <Icon size={18} />
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`absolute left-1/2 -translate-x-1/2 top-11 w-0.5 h-6 ${
                        i < currentIdx ? "bg-[color:var(--brunchi-green)]" : "bg-[color:var(--brunchi-stone)]"
                      }`} />
                    )}
                  </div>
                  <div className="flex-1 pt-1.5">
                    <div className={`font-display text-lg ${current ? "font-semibold" : "font-medium"}`}>{s.label}</div>
                    {current && <div className="text-xs text-[color:var(--brunchi-green-hover)] uppercase tracking-widest mt-0.5">Current step</div>}
                  </div>
                  {done && <CheckCircle2 size={18} className="text-[color:var(--brunchi-green)] mt-3" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 bg-[color:var(--brunchi-cream)] rounded-xl p-5 border border-[color:var(--brunchi-stone)]">
        <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)] mb-3">In this order</div>
        <div className="space-y-2">
          {data.items?.map((i, ix) => (
            <div key={ix} className="flex items-center gap-3 text-sm">
              {i.image && <img src={i.image} alt="" className="w-10 h-10 object-cover rounded" />}
              <div className="flex-1">{i.name}</div>
              <div className="font-mono">× {i.quantity}</div>
            </div>
          ))}
        </div>
      </div>

      {!data.total_eur && (
        <div className="mt-6 text-sm">
          <div className="text-[color:var(--brunchi-ink-soft)] mb-2">Enter the email you used at checkout to see the total.</div>
          <div className="flex gap-2">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="flex-1 px-4 py-2.5 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm" data-testid="track-email" />
            <button onClick={() => load(email)} data-testid="track-verify-btn"
              className="px-5 py-2.5 rounded-full pill-outline text-sm">Verify</button>
          </div>
        </div>
      )}
    </div>
  );
}
